import React, {useEffect, useState} from "react";
import {Box, Button, Radio, TextField, Typography} from "@mui/material";
import {useTranslation} from "react-i18next";

// CSS-in-JS Styles
const styles = {
    options: {
        display: "flex",
        alignItems: "center",
        padding: "10px 15px",
        border: "1px solid #ddd", // Default border
        borderRadius: "8px",
        marginBottom: "10px",
        cursor: "pointer",
        transition: "all 0.3s ease",
    },
    selected: {
        border: "1px solid #0FB694", // Highlight selected border
        backgroundColor: "#F0FFF9", // Subtle green background
    },
};

export default function InputRadio({
                                       data,
                                       addQuestion,
                                       deleteQuestionsAfterIndex,
                                       goToNextQuestion,
                                       isVisible,
                                       addToUpdateQuestions,
                                       deleteFromQuestionToUpdate,
                                   }) {
    const {t} = useTranslation(); // Allow translations using `t`
    const [selectedValue, setSelectedValue] = useState(data?.currentValue || ''); // Track selected radio value
    const [showInput, setShowInput] = useState(false); // Condition to show/hide input field
    const [inputValue, setInputValue] = useState(""); // The value of the input field
    const [showError, setShowError] = useState(false); // Track error state for required fields
    const [inputError, setInputError] = useState(false); // Track error state for input validation
    const language = localStorage.getItem("i18nextLng");

    // Automatically set the input field state on initialization (if a value already exists)
    useEffect(() => {
        if (data?.currentValue) {
            const defaultChoiceKey = Object.keys(data.currentValue)[0]; // Extract the key from currentValue
            const defaultChoice = data?.choices.find(
                (choice) => String(choice?.value) === String(defaultChoiceKey)
            );

            setSelectedValue(defaultChoiceKey); // Set the selected value
            if (defaultChoice?.display_field) {
                setShowInput(true); // Show the input field if `display_field` is true
                setInputValue(data.currentValue[defaultChoiceKey]); // Populate the input field with the value
            } else {
                setShowInput(false); // Hide the input field
            }
        }
    }, [data]);

    const handleOptionClick = (choice) => {
        deleteQuestionsAfterIndex(data);
        deleteFromQuestionToUpdate(data);
        setSelectedValue(choice?.value); // Update selected radio value
        if (choice?.display_field) {
            setShowInput(true); // Show the input field if `display_field` is true
            setInputValue(""); // Reset the input field
        } else {
            setShowInput(false); // Hide the input field for other choices
            setInputValue(""); // Reset any leftover input value
        }
    };

    // Handle the "Next" button click
    const handleNextClick = () => {
        if (data.required && !selectedValue) {
            // Show an error if required and no value is selected
            setShowError(true);
            return;
        }

        if (data && inputValue) {
            addToUpdateQuestions(data, inputValue);
        } else if (!Object.hasOwnProperty.call(data.currentValue, selectedValue)) {
            addToUpdateQuestions(data, selectedChoice.value);
        }

        // If the selected choice has `display_field: true` and the input is empty, show an error
        if (selectedChoice?.display_field && !inputValue) {
            setShowError(true);
            return;
        }

        // Add follow-up questions if the selected option has a `question_object`
        if (selectedChoice?.question_object) {
            addQuestion(selectedChoice.question_object); // Add the follow-up question(s)
        } else {
            // Otherwise, move to the next question
            goToNextQuestion();
        }
    };

    function handleSkipClick() {
        if (data?.associated_step_id === "4c2ea3bd-831a-4a53-8cc7-5ec1b26d628b") {
            if (data?.question_object) {

                addQuestion(data?.question_object);
            } else {
                // Otherwise, move to the next question
                goToNextQuestion(null, true);
            }
        } else {
            goToNextQuestion(null, true);
        }
    }

    // Validate input value
    const validateInput = (value) => {
        if (data.key === "software_type") {
            // For "software_type", accept any text string
            setInputError(false); // No validation error for text
        } else {
            // For other cases, validate that the input is a number > 0
            const numberValue = Number(value);
            if (isNaN(numberValue) || numberValue <= 0) {
                setInputError(true); // Set error if the value is not a number or is <= 0
            } else {
                setInputError(false); // Clear error if the value is valid
            }
        }
    };

    // Handle input change
    const handleInputChange = (e) => {
        const value = e.target.value;

        // If the input is for a numeric field and the value is "0", reject it
        if (data.key !== "software_type" && value === "0") {
            setInputError(true); // Set error state
            setInputValue(""); // Clear the input value
            return; // Exit the function early
        }

        setInputValue(value); // Update the input value
        validateInput(value); // Validate the input
    };

    // Disable the "Next" button if the selected choice has `display_field: true` and the input is empty or invalid
    const selectedChoice = data.choices.find((choice) => String(choice.value) === String(selectedValue));
    const isInputRequired = selectedChoice?.display_field && !inputValue;
    const isDisabled = !selectedValue || isInputRequired || inputError;

    return (
        <Box
            sx={{
                padding: "20px",
                display: "flex",
                flexDirection: "column",
            }}
        >
            {/* Render question */}
            <Typography variant="subtitle1" sx={{mb: 2}}>
                {language === "fr" ? data?.question_fr : data.question}
            </Typography>

            {/* Render radio options */}
            <Box>
                {data?.choices.map((choice) => {
                    const isSelected = String(selectedValue) === String(choice?.value);

                    return (
                        <Box
                            key={choice?.id} // Unique key for each choice
                            onClick={() => handleOptionClick(choice)} // Handle user click
                            sx={{
                                ...styles.options,
                                ...(isSelected ? styles.selected : {}), // Apply `selected` styles
                            }}
                        >
                            <Radio
                                checked={isSelected}
                                onChange={() => handleOptionClick(choice)} // Update selection
                                checkedIcon={
                                    <svg
                                        width="24"
                                        height="24"
                                        viewBox="0 0 24 24"
                                        fill="none"
                                        xmlns="http://www.w3.org/2000/svg"
                                    >
                                        <path
                                            d="M12 21C16.9706 21 21 16.9706 21 12C21 7.02944 16.9706 3 12 3C7.02944 3 3 7.02944 3 12C3 16.9706 7.02944 21 12 21Z"
                                            fill="#0FB694"
                                        />
                                        <path
                                            d="M9 12L11 14L15 10"
                                            stroke="white"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                        />
                                    </svg>
                                }
                            />
                            <Typography
                                variant="body1"
                                sx={{
                                    marginLeft: "10px",
                                    fontWeight: "500",
                                    color: "#555",
                                }}
                            >
                                {t(choice?.value)}
                            </Typography>
                        </Box>
                    );
                })}
            </Box>

            {/* Render input field if `showInput` is true */}
            {showInput && (
                <Box
                    sx={{
                        marginTop: "20px", // Provide spacing between input and radio group
                        maxWidth: "300px", // Optional max width for the input box
                    }}
                >
                    <TextField
                        fullWidth
                        placeholder={
                            data.key === "software_type"
                                ? language === "fr" ? "Entrez un texte" : "Enter Text"
                                : language === "fr" ? "Entrez un nombre" : "Enter a Number"
                        }
                        type={data.key === "software_type" ? "text" : "number"} // Set input type based on data.key
                        value={inputValue}
                        onChange={handleInputChange} // Update the input value
                        error={inputError} // Show error state
                        helperText={
                            inputError
                                ? data.key === "software_type"
                                    ? "" // No error message for text input
                                    : language === "fr"
                                        ? "La valeur doit être ≥ 1"
                                        : "Value must be ≥ 1"
                                : ""
                        } // Error message
                    />
                </Box>
            )}

            {/* Error Message */}
            {showError && (
                <Typography variant="body2" sx={{color: "error.main", mt: 1}}>
                    {t("This field is required")}
                </Typography>
            )}


            <div style={{display: "flex", gap: "10px", alignItems: "center"}}>
                {isVisible(data) && (
                    <Button
                        variant="contained"
                        color="secondary"
                        style={{
                            height: "50px",
                            width: data?.associated_step_id !== "4c2ea3bd-831a-4a53-8cc7-5ec1b26d628b" ? "50%" : "50%", // Each button takes 45% of the container width
                            marginTop: "20px",
                        }}
                        onClick={handleNextClick}
                        disabled={isDisabled} // Disable if required fields are empty
                    >
                        {data?.last_step_question_2 ? t("finish") : t("next")}
                    </Button>
                )}
                {isVisible(data) && (
                    <Button
                        style={{
                            height: "50px",
                            width: "50%", // Each button takes 45% of the container width
                            marginTop: "20px",
                        }}
                        color="secondary"
                        variant="contained"
                        onClick={handleSkipClick}
                    >
                        {t("skip")}
                    </Button>
                )}
            </div>

        </Box>
    );
}